import { FC } from 'react';
import { Link } from 'react-router-dom';
import { HStack, VStack } from '@chakra-ui/react';

import { FeatureName } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BankAccountPicker from 'components/BankAccountPicker';
import { ALL_ACCOUNTS_VALUE } from 'components/BankAccountPicker/constants';
import * as accountingRoutes from 'features/Accounting/routes';

import * as routes from '../../../routes';
import { useBankOverviewSearchParams } from '../hooks/useBankOverviewSearchParams';
import locales from './locales';

// Workaround to have the account picker inner text aligned with the top of the page.
const BANK_ACCOUNT_PICKER_NEGATIVE_MARGIN_TOP = -12;

interface AccountHeaderWithBalanceProps {
  loading: boolean;
  currentBalance: number;
  currency: string;
  nextBalance: number;
}

const AccountHeaderWithBalance: FC<AccountHeaderWithBalanceProps> = ({
  currency,
  currentBalance,
  loading,
  nextBalance,
}) => {
  const { bankAccountId, setBankAccountId } = useBankOverviewSearchParams();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { bankAccounts } = useCompanyBankAccounts();

  const shouldDisplayNextBalance = nextBalance > 0 || currentBalance > 0;

  const handleBankAccountChange = (nextBankAccountId: string | undefined) => {
    setBankAccountId(nextBankAccountId);
  };

  return (
    <HStack
      alignItems="flex-start"
      gap="space-16"
      justifyContent="space-between"
      marginTop={BANK_ACCOUNT_PICKER_NEGATIVE_MARGIN_TOP}
      paddingBottom="space-32"
      wrap="wrap"
    >
      <VStack alignItems="flex-start" gap={0}>
        <BankAccountPicker
          key={bankAccountId}
          onBankAccountChange={handleBankAccountChange}
          selectedBankAccountId={bankAccountId ?? ALL_ACCOUNTS_VALUE}
          withAllAccountsOption={bankAccounts.length > 1}
          withCreateAccountOption={canAccessFeature({
            name: FeatureName.Bank,
          })}
        />

        <Heading
          data-testid="current-balance"
          size="xl"
          variant="number"
          {...(loading && { filter: 'blur(15px)' })}
        >
          {formatAmount({
            currency,
            forcePlusSign: false,
            value: currentBalance / 100,
          })}
        </Heading>
        {shouldDisplayNextBalance ? (
          <Text variant="light" {...(loading && { filter: 'blur(15px)' })}>
            {`${locales.pending} ${formatAmount({
              currency,
              forcePlusSign: false,
              value: (nextBalance - currentBalance) / 100,
            })}`}
          </Text>
        ) : null}
      </VStack>
      <HStack
        marginTop={-BANK_ACCOUNT_PICKER_NEGATIVE_MARGIN_TOP}
        spacing="space-16"
      >
        <Button
          as={Link}
          icon="export"
          iconPosition="left"
          to={{
            pathname: accountingRoutes.exports,
            state: {
              fromBankOverview: true,
            },
          }}
          variant="secondary"
        >
          {locales.bankAccountStatementsLinkLabel}
        </Button>
        {!bankAccountId ? null : (
          <Button
            as={Link}
            icon="wallet"
            iconPosition="left"
            to={routes.getBankAccountDetailsPath(bankAccountId)}
            variant="secondary"
          >
            {locales.bankAccountDetailsLinkLabel}
          </Button>
        )}
      </HStack>
    </HStack>
  );
};

export default AccountHeaderWithBalance;
